import { ReceiptLanguage } from '../../receipt-printer/enums';
import { Peripheral } from '../Peripheral';
import { PeripheralType } from '../types';
import { ConnectionType } from '../types';
import { PrintApiConnectionType, PrintApiImplementationType, PrintApiLanguage, PrintApiPrinter, PrintApiPrinterCreateParams, PrintApiPrinterType } from './types';
import { mapConnectionTypeToPrintApi, mapLanguageToPrintApi } from './utils';

function toPrinterType(peripheralType: PeripheralType): PrintApiPrinterType {
  return peripheralType === PeripheralType.labelPrinter
    ? PrintApiPrinterType.label
    : PrintApiPrinterType.receipt;
}

export interface PrinterMatcher {
  readonly implementationType: PrintApiImplementationType;
  // Service names whose devices this matcher handles
  readonly serviceNames: readonly string[];
  // Returns true if the local device corresponds to the given PrintApiPrinter record.
  // All string fields must be treated as absent if null/undefined/empty.
  matches(local: Peripheral, printApiPrinter: PrintApiPrinter): boolean;
  // Builds the params needed to register this local device in the print API DB.
  // Returns undefined if the device lacks sufficient identifying data to register.
  buildCreateParams(local: Peripheral): PrintApiPrinterCreateParams | undefined;
  // Builds a stable dedup key for the given params. Must use the same identity
  // fields as matches() so that sync tracking stays consistent with device matching.
  buildSyncKey(params: PrintApiPrinterCreateParams): string;
}

// Determines the PrintApiLanguage for a local device.
// ReceiptPrinter subclasses expose a `language` getter; LabelPrinter does not (defaults to ZPL).
function getLocalLanguage(local: Peripheral): PrintApiLanguage {
  const withLanguage = local as Peripheral & { language?: ReceiptLanguage };
  if (withLanguage.language != null) {
    return mapLanguageToPrintApi(withLanguage.language);
  }
  return PrintApiLanguage.zpl;
}

function isPresent(value: string | null | undefined): value is string {
  return value != null && value !== '';
}

export class WebUsbPrinterMatcher implements PrinterMatcher {
  readonly implementationType = PrintApiImplementationType.WebUSB;
  readonly serviceNames = ['UsbReceiptPrinterService', 'UsbLabelPrinterService'] as const;

  matches(local: Peripheral, printApiPrinter: PrintApiPrinter): boolean {
    if (printApiPrinter.implementationType !== PrintApiImplementationType.WebUSB) return false;

    const vendorId = local.metadata?.vendorId;
    const productId = local.metadata?.productId;
    if (!vendorId || !productId) return false;

    return (
      vendorId === printApiPrinter.metadata.vendorId &&
      productId === printApiPrinter.metadata.productId &&
      toPrinterType(local.peripheralType) === printApiPrinter.type
    );
  }

  buildSyncKey(params: PrintApiPrinterCreateParams): string {
    return [params.implementationType, params.type, params.metadata?.vendorId, params.metadata?.productId].join(':');
  }

  buildCreateParams(local: Peripheral): PrintApiPrinterCreateParams | undefined {
    const vendorId = local.metadata?.vendorId;
    const productId = local.metadata?.productId;
    if (!vendorId || !productId) return undefined;

    const language = getLocalLanguage(local);

    return {
      name: local.name,
      connectionType: PrintApiConnectionType.usb,
      implementationType: PrintApiImplementationType.WebUSB,
      language,
      type: toPrinterType(local.peripheralType),
      metadata: {
        vendorId,
        productId,
        model: local.metadata?.model ?? undefined,
        serialNumber: local.metadata?.serialNumber ?? undefined,
      },
    };
  }
}

export class WebviewAppPrinterMatcher implements PrinterMatcher {
  readonly implementationType = PrintApiImplementationType.WebviewApp;
  readonly serviceNames = ['WebviewAppReceiptPrinterService', 'WebviewAppLabelPrinterService'] as const;

  matches(local: Peripheral, printApiPrinter: PrintApiPrinter): boolean {
    if (toPrinterType(local.peripheralType) !== printApiPrinter.type) return false;

    const localConnType = mapConnectionTypeToPrintApi(local.connectionType);
    if (localConnType !== printApiPrinter.connectionType) return false;

    if (localConnType === PrintApiConnectionType.usb) {
      // USB: identity is vendorId + productId. Normalize falsy productId values
      // (undefined/null/0/'') to a single value so they compare equal across sources.
      const vendorId = local.metadata?.vendorId;
      if (!vendorId || vendorId !== printApiPrinter.metadata.vendorId) return false;

      const localProductId = local.metadata?.productId || undefined;
      const apiProductId = printApiPrinter.metadata.productId || undefined;
      return localProductId === apiProductId;
    }

    // Network/BT: identity is address (matched against macAddress or ipAddress)
    const localAddress = local.metadata?.address;
    if (!isPresent(localAddress)) return false;
    const apiMac = printApiPrinter.metadata.macAddress;
    const apiIp = printApiPrinter.metadata.ipAddress;
    return localAddress === apiMac || localAddress === apiIp;
  }

  buildSyncKey(params: PrintApiPrinterCreateParams): string {
    const identityKey = params.connectionType === PrintApiConnectionType.usb
      ? [params.metadata?.vendorId, params.metadata?.productId || ''].join(':')
      : params.metadata?.macAddress ?? params.metadata?.ipAddress;
    return [params.implementationType, params.connectionType, params.type, identityKey].join(':');
  }

  buildCreateParams(local: Peripheral): PrintApiPrinterCreateParams | undefined {
    const vendorId = local.metadata?.vendorId;
    const address = local.metadata?.address;

    // Need at least one identifying field
    if (!vendorId && !isPresent(address)) return undefined;

    // Route the address to the correct field based on connection type.
    // LAN/wifi connections report an IP; BT and others report a MAC.
    const isLanConnection = local.connectionType === ConnectionType.lan;

    return {
      name: local.name,
      connectionType: mapConnectionTypeToPrintApi(local.connectionType),
      implementationType: PrintApiImplementationType.WebviewApp,
      language: getLocalLanguage(local),
      type: toPrinterType(local.peripheralType),
      metadata: {
        vendorId: vendorId ?? undefined,
        productId: local.metadata?.productId ?? undefined,
        macAddress: isPresent(address) && !isLanConnection ? address : undefined,
        ipAddress: isPresent(address) && isLanConnection ? address : undefined,
        model: local.metadata?.model ?? undefined,
        serialNumber: local.metadata?.serialNumber ?? undefined,
      },
    };
  }
}

export class NativePrinterMatcher implements PrinterMatcher {
  readonly implementationType = PrintApiImplementationType.Native;
  readonly serviceNames = ['NativeReceiptPrinterService', 'NativeLabelPrinterService'] as const;

  matches(local: Peripheral, printApiPrinter: PrintApiPrinter): boolean {
    if (toPrinterType(local.peripheralType) !== printApiPrinter.type) return false;
    if (mapConnectionTypeToPrintApi(local.connectionType) !== printApiPrinter.connectionType) return false;

    const localAddress = local.metadata?.address;
    const apiMac = printApiPrinter.metadata.macAddress;

    // Address-based match (network/BT printers)
    if (isPresent(localAddress) && isPresent(apiMac)) {
      return localAddress === apiMac;
    }

    // Fallback: vendorId for USB printers (no network address)
    const localVendorId = local.metadata?.vendorId;
    if (!localVendorId) return false;
    return localVendorId === printApiPrinter.metadata.vendorId;
  }

  buildSyncKey(params: PrintApiPrinterCreateParams): string {
    return [params.implementationType, params.type, params.metadata?.vendorId].join(':');
  }

  buildCreateParams(local: Peripheral): PrintApiPrinterCreateParams | undefined {
    const vendorId = local.metadata?.vendorId;
    const address = local.metadata?.address;

    // Need at least one identifying field
    if (!vendorId && !isPresent(address)) return undefined;

    return {
      name: local.name,
      connectionType: mapConnectionTypeToPrintApi(local.connectionType),
      implementationType: PrintApiImplementationType.Native,
      language: getLocalLanguage(local),
      type: toPrinterType(local.peripheralType),
      metadata: {
        vendorId: vendorId ?? undefined,
        macAddress: isPresent(address) ? address : undefined,
        model: local.metadata?.model ?? undefined,
      },
    };
  }
}

// Centralized registry — used by PeripheralServicePrinterManager and PrintApiService
export const printerMatchers: PrinterMatcher[] = [
  new WebUsbPrinterMatcher(),
  new WebviewAppPrinterMatcher(),
  new NativePrinterMatcher(),
];
